import { T, useI18n } from '@/i18n';
import { Icon } from '@/ui/icons';
import * as Popover from '@radix-ui/react-popover';
import { type Locale, addDays, endOfMonth, format, startOfMonth, startOfWeek } from 'date-fns';
import { enGB, lt, nb, pl } from 'date-fns/locale';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { DayPicker, type Modifiers } from 'react-day-picker';
import 'react-day-picker/style.css';

/** Norwegian names for the day picker's own controls; the library's are English («Go to the Previous Month») (#272 B-W02) */
export const calendarLabels = {
  labelDayButton: (day: Date, m: Modifiers, o?: { locale?: Locale }) => {
    const name = format(day, 'PPPP', o?.locale ? { locale: o.locale } : {});
    const said = m.today ? T('I dag, {day}', { day: name }) : name;
    return m.selected ? T('{day}, valgt', { day: said }) : said;
  },
  labelGridcell: (day: Date, m?: Modifiers, o?: { locale?: Locale }) => {
    const name = format(day, 'PPPP', o?.locale ? { locale: o.locale } : {});
    return m?.today ? T('I dag, {day}', { day: name }) : name;
  },
  labelNav: () => T('Bla mellom månedene'),
  labelPrevious: () => T('Forrige måned'),
  labelNext: () => T('Neste måned'),
  labelMonthDropdown: () => T('Måned'),
  labelYearDropdown: () => T('År'),
};
/** Phones get the calendar as a bottom sheet (styles.css, same width) instead of a popover by its button. */
export const sheetCalendar = () =>
  typeof matchMedia === 'function' && matchMedia('(max-width: 640px)').matches;

/** The desktop popover's full height: heading, month row, weekdays, six weeks and the footer. */
const FULL = 470;

export type PeriodMode = 'day' | 'week' | 'two' | 'three' | 'month';
export function periodRange(day: Date, mode: PeriodMode) {
  const from =
    mode === 'month'
      ? startOfMonth(day)
      : mode === 'day'
        ? day
        : addDays(startOfWeek(day, { weekStartsOn: 1 }), mode === 'three' ? -7 : 0);
  return {
    from,
    to:
      mode === 'month'
        ? endOfMonth(day)
        : addDays(from, mode === 'three' ? 20 : mode === 'two' ? 13 : mode === 'week' ? 6 : 0),
  };
}

/** A calendar selection represents the same period as the view it controls. */
export function DateJump({
  value,
  onChange,
  children,
  month = false,
  period,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  month?: boolean;
  period?: PeriodMode;
  /** the owning control's class for the trigger (PeriodControl); `datejump` stays for the icon rules */
  className?: string;
}) {
  const locale = useI18n((s) => s.locale);
  const calendarLocale = { nb, en: enGB, lt, pl }[locale];
  const mode = period ?? (month ? 'month' : 'day');
  const initial = () => new Date(`${value.length === 7 ? `${value}-01` : value}T12:00:00`);
  const [open, setOpen] = useState(false);
  // a bottom sheet on a phone: everything fits, so no room to measure and nothing to scroll (#272 B-W04)
  const sheet = open && sheetCalendar();
  // The room for the calendar, measured from its own button while it is open: below, or above when
  // there is more. Radix's own available-height variable was wrong under browser zoom (326 px said,
  // 231 px free at 200 %), and the calendar ran off the screen (#154 VF-07). It scrolls inside that
  // room, and its footer with «Vis perioden» stays at the bottom of what shows.
  const trigger = useRef<HTMLButtonElement>(null);
  const [room, setRoom] = useState<{ side: 'bottom' | 'top'; height: number } | null>(null);
  // Less room than the whole calendar needs (about 470 px with its footer): nothing in it stays pinned,
  // and «Vis perioden» sits in its top row, so focus can scroll every day and arrow into view (review
  // UI01-2: a pinned footer covered the focused day at 250x350)
  const short = !sheet && !!room && room.height < 470;
  // Whatever has focus in the calendar is scrolled into its view: Radix moves focus with preventScroll
  // (opening, and Tab wrapping round to «Vis perioden»), and the short layout settles after the first
  // focus (review UI01-2)
  const content = useRef<HTMLDivElement>(null);
  const opening = useRef(false);
  const keepInView = () => {
    // the first focus on open places the scroll itself, below (#272 B-V02)
    if (opening.current) return;
    const a = document.activeElement;
    if (a instanceof HTMLElement && content.current?.contains(a))
      a.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };
  // «Vis perioden» is the next step from any day (review UI01-4): the day picker keeps its own Tab stop
  // on one day of the month, so after choosing a day further on (7 November, with 1 November the stop)
  // the browser's Tab left the calendar and closed it unapplied. And it keeps focus when the room moves
  // it between the top row and the foot (390x844 to 1440x900 dropped focus to the page).
  const apply = useRef<HTMLButtonElement>(null);
  const applyFocused = useRef(false);
  const onKeyDown = (e: { key: string; shiftKey: boolean; preventDefault: () => void }) => {
    if (e.key !== 'Tab' || e.shiftKey) return;
    if (!(document.activeElement instanceof HTMLElement && document.activeElement.closest('.rdp-day')))
      return;
    e.preventDefault();
    // after Radix's own Tab handler on the same element has run: focusing now, it saw the button as its
    // last stop and wrapped round to the first control
    queueMicrotask(() => apply.current?.focus());
  };
  useEffect(() => {
    if (!open) applyFocused.current = false;
  }, [open]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: when the layout moves the button, focus follows it
  useEffect(() => {
    if (open && applyFocused.current && document.activeElement !== apply.current) apply.current?.focus();
  }, [short]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: again once the room, and so the layout, is known
  useEffect(() => {
    if (!open) return;
    // with a day focused (opening puts it on the period's first day), the whole chosen period comes into
    // view: its last day first, then its first, so a period taller than the room shows from its start
    // (#272 B-U05, B-U06: at 320 the chosen week opened below the fold, three weeks were cut after one row)
    const id = requestAnimationFrame(() => {
      if (document.activeElement?.closest('.rdp-day'))
        content.current
          ?.querySelector('.rdp-range_end')
          ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      keepInView();
    });
    return () => cancelAnimationFrame(id);
  }, [open, room]);
  useEffect(() => {
    if (!open) return;
    if (sheetCalendar()) {
      setRoom(null);
      return;
    }
    let retried = false;
    const measure = () => {
      const r = trigger.current?.getBoundingClientRect();
      if (!r) return;
      // the phone's bottom bar covers the foot of the window: the room ends where it starts (#272 B-T04)
      const bar = document.getElementById('pnav')?.getBoundingClientRect();
      const floor = bar && bar.height > 0 ? Math.min(bar.top, window.innerHeight) : window.innerHeight;
      const below = floor - r.bottom;
      // and the app's top bar covers its head: above, the room starts under it (#272 B-U05)
      const head = document.getElementById('top')?.getBoundingClientRect();
      const top = head && head.height > 0 ? Math.max(0, head.bottom) : 0;
      const above = r.top - top;
      // neither side holds the whole calendar (Kontroll at 820, its button low on the page): bring the button up
      // under the top bar first, so it opens below at full height instead of scrolling inside itself (#272 F-02)
      if (
        Math.max(below, above) < FULL &&
        r.top - top > 8 &&
        !retried &&
        typeof window.scrollBy === 'function'
      ) {
        retried = true;
        window.scrollBy({ top: r.top - top - 8 });
        measure();
        return;
      }
      setRoom({
        side: below >= above ? 'bottom' : 'top',
        // whole pixels and one spare: a fractional fit made Radix flip it to the other side (#272 B-T04)
        height: Math.max(96, Math.floor(Math.max(below, above)) - 6 - 12 - 1),
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open]);
  const [draft, setDraft] = useState(initial);
  const [visible, setVisible] = useState(initial);
  const range = periodRange(draft, mode);
  const label = T(
    mode === 'month'
      ? 'Måned'
      : mode === 'three'
        ? '3 uker'
        : mode === 'two'
          ? 'To uker'
          : mode === 'week'
            ? 'Uke'
            : 'Dag',
  );
  const summary =
    mode === 'month'
      ? format(range.from, 'LLLL yyyy', { locale: calendarLocale })
      : `${format(range.from, 'd. MMM', { locale: calendarLocale })}${mode === 'day' ? '' : ` – ${format(range.to, 'd. MMM yyyy', { locale: calendarLocale })}`}`;
  const actions = (
    <div className={short ? 'datejump-footer top' : 'datejump-footer'}>
      <span aria-live="polite">{summary}</span>
      <button
        ref={apply}
        type="button"
        className="btn"
        onFocus={() => {
          applyFocused.current = true;
        }}
        onBlur={(e) => {
          // a move to another control; the layout taking the button away blurs with no target
          if (e.relatedTarget) applyFocused.current = false;
        }}
        onClick={() => {
          onChange(
            format(mode === 'three' ? addDays(range.from, 7) : range.from, month ? 'yyyy-MM' : 'yyyy-MM-dd'),
          );
          setOpen(false);
        }}
      >
        {T('Vis perioden')}
      </button>
    </div>
  );
  return (
    <Popover.Root
      // modal on a phone: the dimmed page behind the sheet takes no taps or scrolls (#272 B-X01)
      modal={sheetCalendar()}
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setDraft(initial());
          setVisible(initial());
        }
      }}
    >
      <Popover.Trigger asChild>
        <button
          ref={trigger}
          type="button"
          className={className ? `datejump ${className}` : 'datejump'}
          title={T('Velg dato')}
        >
          <Icon name="calendar" />
          {children}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          ref={content}
          onFocus={keepInView}
          // the chosen period's first day takes focus, not the month arrow, so the period is what shows
          // and the arrow keys move from it (#272 B-U05, B-U06)
          onOpenAutoFocus={(e) => {
            const first = content.current?.querySelector<HTMLElement>('.rdp-range_start button');
            if (!first) return;
            e.preventDefault();
            opening.current = true;
            first.focus({ preventScroll: true });
            if (sheet) {
              opening.current = false;
              return;
            }
            // show the period's last week; the month and its arrows stay pinned above it (#272 B-V02).
            // The day picker focuses its day again after mounting, which scrolls the box; place it after that.
            const place = () => {
              const box = content.current;
              const end = box?.querySelector<HTMLElement>('.rdp-range_end') ?? first;
              if (!box || !end) return;
              const b = box.getBoundingClientRect();
              const need = end.getBoundingClientRect().bottom - b.bottom + box.scrollTop;
              // the month row stays pinned at the top (styles.css), so the whole period can scroll into view
              box.scrollTop = Math.max(0, need + 8);
            };
            place();
            setTimeout(() => {
              place();
              opening.current = false;
            }, 120);
          }}
          onKeyDown={onKeyDown}
          className="pop datejump-pop"
          side={room?.side ?? 'bottom'}
          sideOffset={6}
          // 5 px at the sides: under 360 px the seven 44 px days need all but 10 px of the window (#272 B-S01)
          collisionPadding={{ top: 12, bottom: 12, left: 5, right: 5 }}
          style={room && !sheet ? { maxHeight: room.height } : undefined}
          aria-label={label}
        >
          <div className="datejump-heading">{label}</div>
          {short && actions}
          <DayPicker
            locale={calendarLocale}
            labels={calendarLabels}
            weekStartsOn={1}
            showOutsideDays
            fixedWeeks
            month={visible}
            onMonthChange={(d) => {
              setVisible(d);
              if (mode === 'month') setDraft(d);
            }}
            captionLayout="dropdown"
            startMonth={new Date(2000, 0)}
            endMonth={new Date(2100, 11)}
            modifiers={{
              selected: range,
              range_start: range.from,
              range_end: range.to,
              range_middle: (d) => d > range.from && d < range.to,
            }}
            onDayClick={(d) => setDraft(d)}
            onDayKeyDown={(d, _, e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setDraft(d);
              }
            }}
          />
          {!short && actions}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export function validPeriod(value: string, month: boolean): boolean {
  if (!(month ? /^\d{4}-(0[1-9]|1[0-2])$/ : /^\d{4}-(0[1-9]|1[0-2])-\d{2}$/).test(value)) return false;
  const full = month ? `${value}-01` : value;
  const parsed = new Date(`${full}T12:00:00Z`);
  return (
    Number(full.slice(0, 4)) > 0 &&
    Number.isFinite(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === full
  );
}
